import { create } from "zustand";
import { SharedIntegrationsDataType } from "@/components/DetailsSection/types";
import {
  ANALYSIS_PROVIDER_NAMES,
  INTEGRATION_TYPE
} from "@/Pages/Integrations/types";
import { IntegrationsErrorDialogSteps } from "@/shared";

interface IntegrationDialogState {
  step: IntegrationsErrorDialogSteps;
  errorCode: number;
  resourceData: SharedIntegrationsDataType | null;
  sourceStep:
    | IntegrationsErrorDialogSteps.Add
    | IntegrationsErrorDialogSteps.Edit
    | IntegrationsErrorDialogSteps.Retry
    | null;
  fromError: boolean;
  initialIntegrationType: INTEGRATION_TYPE | null;
  generatedApiKey: string | null;

  openAddDialog: (preselectedType: INTEGRATION_TYPE) => void;
  goToSftpForm: () => void;
  goToApiForm: () => void;
  goToConveyIntegrationsForm: () => void;
  goToVDOIntegrationsForm: () => void;
  goBackToTypeSelect: () => void;
  showGeneratedKey: (plainTextKey: string) => void;
  openEditDialog: (data: SharedIntegrationsDataType) => void;
  openEditAnalysisProvidersDialog: (data: SharedIntegrationsDataType) => void;
  showError: (
    code: number,
    data: SharedIntegrationsDataType | null,
    source:
      | IntegrationsErrorDialogSteps.Add
      | IntegrationsErrorDialogSteps.Edit
      | IntegrationsErrorDialogSteps.Retry
      | null
  ) => void;
  goToEdit: () => void;
  closeAll: () => void;
  reset: () => void;
}

export const useIntegrationsDialogStore = create<IntegrationDialogState>()(
  set => ({
    step: IntegrationsErrorDialogSteps.Closed,
    errorCode: 0,
    resourceData: null,
    sourceStep: null,
    fromError: false,
    initialIntegrationType: null,
    generatedApiKey: null,

    openAddDialog: preselectedType =>
      set({
        step: IntegrationsErrorDialogSteps.TypeSelect,
        errorCode: 0,
        resourceData: null,
        initialIntegrationType: preselectedType,
        generatedApiKey: null
      }),

    goToSftpForm: () =>
      set({
        step: IntegrationsErrorDialogSteps.Add
      }),

    goToApiForm: () =>
      set({
        step: IntegrationsErrorDialogSteps.AddApi
      }),

    goToConveyIntegrationsForm: () =>
      set({
        step: IntegrationsErrorDialogSteps.ConveyIntegrations
      }),

    goToVDOIntegrationsForm: () =>
      set({
        step: IntegrationsErrorDialogSteps.VDOIntegrations
      }),

    goBackToTypeSelect: () =>
      set({
        step: IntegrationsErrorDialogSteps.TypeSelect
      }),

    showGeneratedKey: plainTextKey =>
      set({
        step: IntegrationsErrorDialogSteps.ApiKeyGenerated,
        generatedApiKey: plainTextKey
      }),

    openEditDialog: data =>
      set({
        step: IntegrationsErrorDialogSteps.Edit,
        errorCode: 0,
        resourceData: data
      }),

    openEditAnalysisProvidersDialog: data => {
      const nextStep =
        data?.provider === ANALYSIS_PROVIDER_NAMES.Convey
          ? IntegrationsErrorDialogSteps.EditConvey
          : IntegrationsErrorDialogSteps.EditVdo;

      set(state => ({
        ...state,
        step: nextStep,
        errorCode: 0,
        resourceData: data
      }));
    },

    showError: (code, data, source) =>
      set({
        step: IntegrationsErrorDialogSteps.Error,
        sourceStep: source,
        errorCode: code,
        resourceData: data
      }),

    goToEdit: () =>
      set({
        step: IntegrationsErrorDialogSteps.Edit,
        fromError: true
      }),

    closeAll: () =>
      set({
        step: IntegrationsErrorDialogSteps.Closed,
        resourceData: null,
        fromError: false,
        initialIntegrationType: null,
        generatedApiKey: null
      }),

    reset: () =>
      set({
        step: IntegrationsErrorDialogSteps.Closed,
        errorCode: 0,
        resourceData: null,
        initialIntegrationType: null,
        generatedApiKey: null
      })
  })
);
